/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Observable } from "@vienna-hypertext/observable";

import { AtLeast, Coordinates } from "@vienna/common/src";
import { BlockSnapshot, PlateWindowSnapshot } from "@vienna/common/src/entities/block-snapshot.entity";

import { Plate } from "../../plate/Plate";
import { BasicBlockMethods } from "src/classes/block/block.interfaces";
import { BasePlate } from "src/classes/plate/BasePlate/BasePlate";
import { PlateSocket } from "src/classes/plate/PlateSocket/PlateSocket";
import { PositionUtils } from "src/classes/utilities/PositionUtils";

export class PlateWindowBlock extends Observable<PlateWindowSnapshot> implements BasicBlockMethods {
  embeddedPlate: BasePlate | null;

  plateSocket: PlateSocket | null;

  constructor(snapshot: PlateWindowSnapshot, plateSocket: PlateSocket | null) {
    super(snapshot);
    this.embeddedPlate = snapshot.initialPlateSnapshot ? new BasePlate(snapshot.initialPlateSnapshot) : null;
    this.plateSocket = plateSocket;
  }

  /* -------------------------------------------------- */
  /*                    Move & Resize                   */
  /* -------------------------------------------------- */
  public move(top: number, left: number): void {
    if (!this.plateSocket) return;
    this.setState({ top: PositionUtils.snapToGrid(top), left: PositionUtils.snapToGrid(left) });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  }

  public resize(width: number, height: number): void {
    if (!this.plateSocket) return;
    this.setState({
      width: PositionUtils.snapToGrid(width),
      height: PositionUtils.snapToGrid(height),
    });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  }

  public changeZIndex(zIndex: number): void {
    if (!this.plateSocket) return;
    this.setState({ zIndex });
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  }

  /* -------------------------------------------------- */
  /*                   Socket Update                    */
  /* -------------------------------------------------- */
  // note: this function is a little dangerous, look for improvements.
  public handleIncomingSocketUpdate(snapshot: AtLeast<BlockSnapshot, "id">): void {
    this.setState({ ...snapshot } as Partial<PlateWindowSnapshot>);
  }
  /* -------------------------------------------------- */
  /*                   Like & Connect                   */
  /* -------------------------------------------------- */

  public like(): void {
    if (!this.embeddedPlate) return;
    this.embeddedPlate.like();
    this.setState({ isLiked: this.embeddedPlate.snapshot.isLiked }); // purely for UI
  }

  public connect(toPlate: Plate | BasePlate): void {
    this.embeddedPlate?.connect(toPlate);
  }

  public get forbiddenConnectionIds(): string[] {
    return [this.snapshot.plateId, this.embeddedPlate?.snapshot.id || ""];
  }

  /* -------------------------------------------------- */
  /*                 Open Drilled View                  */
  /* -------------------------------------------------- */
  public open(): void {
    this.embeddedPlate?.openInDrilledView();
  }

  public openInNewTab(): void {
    this.embeddedPlate?.openInNewTab();
  }

  public copyLink(): void {
    this.embeddedPlate?.copyLink();
  }

  /* -------------------------------------------------- */
  /*                       Utils                        */
  /* -------------------------------------------------- */
  public get coordinates(): Coordinates {
    return {
      top: this.snapshot.top,
      left: this.snapshot.left,
      width: this.snapshot.width,
      height: this.snapshot.height,
    };
  }
}
